import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { VxIconComponent } from '@ui/icon/icon.component';
import {
  IncidentSchemaPriority,
  IncidentSchemaState,
} from 'src/app/models/incident-schema';
import { IncidentPriorityLabelComponent } from '../incident-priority-label/incident-priority-label.component';
import { IncidentStatusLabelComponent } from '../incident-status-label/incident-status-label.component';

/** What happened to the incident, as the toast body shows it. */
export type IncidentActivityChange =
  | {
      kind: 'created';
      state?: IncidentSchemaState;
      priority?: IncidentSchemaPriority;
    }
  | { kind: 'state'; from: IncidentSchemaState; to: IncidentSchemaState }
  | {
      kind: 'priority';
      from: IncidentSchemaPriority;
      to: IncidentSchemaPriority;
    };

/**
 * Body of a realtime incident toast (rendered through `VxToastContent`): the
 * incident's number and title, then the change in the same state/priority labels the
 * incident list uses — "from → to" for a change, both labels for a new
 * incident.
 */
@Component({
  selector: 'app-incident-activity-toast',
  templateUrl: './incident-activity-toast.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    VxIconComponent,
    IncidentPriorityLabelComponent,
    IncidentStatusLabelComponent,
  ],
  host: { class: 'flex min-w-0 flex-col gap-1.5 font-brand' },
})
export class IncidentActivityToastComponent {
  readonly incidentId = input.required<number>();

  readonly incidentTitle = input<string>();

  readonly change = input.required<IncidentActivityChange>();

  /** The incident's schema states, for the status label's progress arc. */
  readonly states = input<readonly IncidentSchemaState[]>([]);

  protected readonly created = computed(() => {
    const change = this.change();
    return change.kind === 'created' ? change : undefined;
  });

  protected readonly stateChange = computed(() => {
    const change = this.change();
    return change.kind === 'state' ? change : undefined;
  });

  protected readonly priorityChange = computed(() => {
    const change = this.change();
    return change.kind === 'priority' ? change : undefined;
  });
}
